import { NmSegmentControl } from "../NmSegmentControl";
import { IoOptionsOutline } from "react-icons/io5";
import Preview from "./ChartLibraryPreview";
import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { AvailableChart } from "../../services/api";
import {
  OPTIONAL_PERFORMANCE_INSIGHTS,
  PERFORMANCE_INSIGHT_DEFS,
  type PerformanceInsightType,
} from "./PerformanceInsightWidgets";
import {
  LIBRARY_GROUPS,
  LIBRARY_TYPES,
  libraryVariantMatches,
  orderDashboardItems,
  type DashboardLibraryItem,
} from "../../utils/dashboardChartOrder";
import "./chart-library.css";
import ChartOrderList from "../reorder/ChartOrderList";

interface Props {
  available: AvailableChart[];
  layoutItems: DashboardLibraryItem[];
  adding: number | null;
  onAdd: (id: number, setting?: Record<string, unknown>) => Promise<boolean>;
  onAddInsight: (type: PerformanceInsightType) => void;
  onRemoveInsight: (type: PerformanceInsightType) => void;
  onSaveOrder: (keys: string[]) => Promise<void>;
  onConfigure: (key: string) => void;
  onClose: () => void;
  mobile?: boolean;
}
type CatalogItem = {
  type: string;
  id?: number;
  name?: string;
  description?: string;
};
export function ChartLibraryPanel(props: Props) {
  const { t, i18n } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null),
    titleId = useId();
  const [tab, setTab] = useState<"all" | "added" | "arrange">("all");
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState(props.layoutItems.map((item) => item.key));
  const [detail, setDetail] = useState<CatalogItem | null>(null);
  const [metric, setMetric] = useState("distance"),
    [period, setPeriod] = useState("week"),
    [zone, setZone] = useState("time_in_zone_hr");
  const [sport, setSport] = useState("0");
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false),
    [error, setError] = useState<"saveFailed" | "addFailed" | null>(null);
  useEffect(() => {
    const node = dialog.current;
    node?.showModal();
    return () => node?.close();
  }, []);
  const catalog = Array.from(
    new Map(
      [
        ...props.available.map((item) => ({
          type: item.chart_type,
          id: item.id,
          name: item.name,
        })),
        ...PERFORMANCE_INSIGHT_DEFS.filter((item) =>
          OPTIONAL_PERFORMANCE_INSIGHTS.includes(item.type)
        ).map((item) => ({
          type: item.type,
          name: t(item.titleKey),
          description: t(item.descriptionKey),
        })),
      ].map((item) => [item.type, item as CatalogItem])
    ).values()
  );
  const title = (item: CatalogItem) =>
    t(`charts.library.types.${item.type}.title`, {
      defaultValue: item.name ?? item.type,
    });
  const description = (item: CatalogItem) =>
    t(`charts.library.types.${item.type}.description`, {
      defaultValue: item.description ?? "",
    });
  const q = search.trim().toLocaleLowerCase(),
    matches = (s: string) => s.toLocaleLowerCase().includes(q);
  const filtered = catalog.filter(
    (item) => matches(title(item)) || matches(description(item))
  );
  const open = (item: CatalogItem) => {
    setError(null);
    setDetail(item);
    setMetric("distance");
    setPeriod("week");
    setZone("time_in_zone_hr");
    setSport("0");
  };
  const chosenSetting: Record<string, unknown> | undefined =
    detail?.type === "periodic_chart"
      ? {
          workout_types: [Number(sport)],
          workout_summery: {
            workout_summery_types: metric,
            group_by: period,
            show_workout_summery: true,
          },
        }
      : detail?.type === "time_in_zone"
      ? { zone_type: zone, workout_types: [Number(sport)] }
      : undefined;
  const existing = detail
    ? props.layoutItems.find((item) => item.type === detail.type && libraryVariantMatches(detail.type, item.setting, chosenSetting))
    : undefined;
  const items = orderDashboardItems(props.layoutItems, draft),
    dirty =
      items.map((item) => item.key).join("|") !==
      props.layoutItems.map((item) => item.key).join("|");
  const dismiss = () => {
    if (!saving && !dragging) props.onClose();
  };
  const add = async (item: CatalogItem, setting?: Record<string, unknown>) => {
    if (props.adding !== null) return;
    setError(null);
    try {
      if (item.id !== undefined) {
        if (!(await props.onAdd(item.id, setting))) setError("addFailed");
      } else props.onAddInsight(item.type as PerformanceInsightType);
    } catch {
      setError("addFailed");
    }
  };
  const choices = (
    label: string,
    values: string[],
    value: string,
    change: (v: string) => void,
    prefix: string
  ) => (
    <label className="chart-library__field">
      <span>{label}</span>
      <select value={value} onChange={(e) => change(e.target.value)}>
        {values.map((v) => (
          <option key={v} value={v}>
            {t(`${prefix}.${v}`)}
          </option>
        ))}
      </select>
    </label>
  );
  return createPortal(
    <dialog
      ref={dialog}
      className="chart-library"
      dir={i18n.dir()}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        dismiss();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <header className="chart-library__header">
        {detail && (
          <button
            type="button"
            className="chart-library__icon"
            aria-label={t("charts.library.all")}
            onClick={() => setDetail(null)}
          >
            ‹
          </button>
        )}
        <div>
          <h2 id={titleId}>
            {detail ? title(detail) : t("charts.library.title")}
          </h2>
          <p>
            {detail
              ? t("charts.library.details")
              : t("charts.library.subtitle")}
          </p>
        </div>
        <button
          type="button"
          className="chart-library__icon"
          onClick={dismiss}
          disabled={saving || dragging}
          aria-label={t("charts.library.close")}
        >
          ×
        </button>
      </header>
      {!detail && (
        <>
          {tab !== "arrange" && (
            <input
              className="chart-library__search"
              aria-label={t("charts.library.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("charts.library.search")}
            />
          )}
          <fieldset
            className="chart-library__tabs"
            disabled={saving || dragging}
          >
            <NmSegmentControl
              variant="track"
              fullWidth
              value={tab}
              ariaLabel={t("charts.library.title")}
              options={(["all", "added", "arrange"] as const).map((value) => ({
                value,
                label: t(
                  `charts.library.${value === "added" ? "addedTab" : value}`
                ),
              }))}
              onChange={(value) => {
                if (saving || dragging) return;
                setTab(value);
                setSearch("");
                setDraft(props.layoutItems.map((item) => item.key));
                setError(null);
              }}
            />
          </fieldset>
        </>
      )}
      <div
        className={`chart-library__scroll ${
          !detail && tab === "arrange" ? "chart-library__scroll--arrange" : ""
        }`}
      >
        {detail ? (
          <div className="chart-library__detail">
            <Preview
              type={detail.type}
              large
              setting={existing?.setting ?? chosenSetting}
            />
            <p>{description(detail)}</p>
            {detail.type === "periodic_chart" && (
              <>
                {choices(
                  t("charts.library.metric"),
                  [
                    "distance",
                    "total_time",
                    "tss_actual",
                    "total_ascent",
                    "work",
                    "calories",
                    "if_actual",
                    "total_descent",
                  ],
                  metric,
                  setMetric,
                  "charts.library.metrics"
                )}
                {choices(
                  t("charts.library.group"),
                  ["day", "week", "month"],
                  period,
                  setPeriod,
                  "charts.library.periods"
                )}
              </>
            )}
            {detail.type === "time_in_zone" &&
              
              choices(
                t("charts.library.metric"),
                [
                  "time_in_zone_hr",
                  "time_in_zone_pw",
                  "time_in_zone_sd",
                  "time_in_zone_cd",
                ],
                zone,
                setZone,
                "charts.library.zones"
              )}
            {["periodic_chart", "time_in_zone"].includes(detail.type) && choices(t("charts.library.sport"), ["0", "1", "3"], sport, setSport, "charts.library.sports")}
            {existing && (
              <span className="chart-library__status">
                ✓ {t("charts.library.added")}
              </span>
            )}
            {existing?.configurable && (
              <button
                type="button"
                onClick={() => props.onConfigure(existing.key)}
              >
                {t("charts.library.configure")}
              </button>
            )}
            {existing &&
              OPTIONAL_PERFORMANCE_INSIGHTS.includes(
                detail.type as PerformanceInsightType
              ) && (
                <button
                  type="button"
                  onClick={() =>
                    props.onRemoveInsight(detail.type as PerformanceInsightType)
                  }
                >
                  {t("charts.library.remove")}
                </button>
              )}
          </div>
        ) : tab === "all" ? (
          <>
            {LIBRARY_GROUPS.map((group) => {
              const rows = filtered.filter(
                (item) => (LIBRARY_TYPES[item.type]?.group ?? "other") === group
              );
              return rows.length ? (
                <section key={group}>
                  <h3>{t(`charts.library.groups.${group}`)}</h3>
                  {rows.map((item) => {
                    const count = props.layoutItems.filter(
                        (card) => card.type === item.type
                      ).length,
                      configurable =
                        item.type === "periodic_chart" ||
                        item.type === "time_in_zone";
                    return (
                      <div className="chart-library__row" key={item.type}>
                        <button
                          type="button"
                          className="chart-library__copy"
                          aria-label={`${t("charts.library.details")} · ${title(
                            item
                          )}`}
                          onClick={() => open(item)}
                        >
                          <Preview
                            type={item.type}
                            setting={
                              "setting" in item
                                ? (item.setting as Record<string, unknown>)
                                : undefined
                            }
                          />
                          <span>
                            <strong>{title(item)}</strong>
                            <small>{description(item)}</small>
                            {count > 0 && (
                              <small>{t("charts.library.addedCount", {count})}</small>
                            )}
                          </span>
                        </button>

                        <button
                          type="button"
                          className={`chart-library__add ${
                            count ? "added" : ""
                          }`}
                          aria-label={`${
                            count && !configurable
                              ? t("charts.library.added")
                              : configurable
                              ? t("charts.library.details")
                              : t("charts.library.add")
                          } · ${title(item)}`}
                          disabled={
                            props.adding !== null ||
                            (!configurable && count > 0)
                          }
                          onClick={() =>
                            configurable ? open(item) : void add(item)
                          }
                        >
                          <span aria-hidden="true">
                            {" "}
                            {props.adding === item.id && item.id !== undefined
                              ? "…"
                              : count && !configurable
                              ? "✓"
                              : "+"}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </section>
              ) : null;
            })}
            {!filtered.length && (
              <p className="chart-library__empty">
                {t("charts.library.noMatch")}
              </p>
            )}
          </>
        ) : tab === "arrange" ? (
          <ChartOrderList
            items={items}
            locale={i18n.language}
            disabled={saving}
            onOrderChange={setDraft}
            onDraggingChange={setDragging}
            labels={{
              hint: t("charts.library.arrangeHint"),
              keyboardHint: t("charts.library.keyboardHint"),
              empty: t("charts.library.noneAdded"),
              drag: (title) => t("charts.library.drag", { title }),
              moveUp: (title) => t("charts.library.moveUp", { title }),
              moveDown: (title) => t("charts.library.moveDown", { title }),
              pickedUp: (title) => t("charts.library.pickedUp", { title }),
              dropped: (title, position) =>
                t("charts.library.dropped", { title, position }),
              cancelled: t("charts.library.dragCancelled"),
            }}
          />
        ) : (
          <>
            {props.layoutItems
              .filter((item) => matches(item.title))
              .map((item) => (
                <div className="chart-library__row layout" key={item.key}>
                  <Preview
                    type={item.type}
                    setting={
                      "setting" in item
                        ? (item.setting as Record<string, unknown>)
                        : undefined
                    }
                  />
                  <strong>{item.title}</strong>
                  {item.configurable && (
                    <button
                      type="button"
                      className="chart-library__icon"
                      aria-label={`${t("charts.library.configure")} · ${
                        item.title
                      }`}
                      onClick={() => props.onConfigure(item.key)}
                    >
                      <IoOptionsOutline aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
            {!props.layoutItems.some((item) => matches(item.title)) && (
              <p className="chart-library__empty">
                {t(
                  props.layoutItems.length
                    ? "charts.library.noMatch"
                    : "charts.library.noneAdded"
                )}
              </p>
            )}
          </>
        )}
      </div>
      <footer className="chart-library__footer">
        {error && <p role="alert">{t(`charts.library.${error}`)}</p>}
        <button
          type="button"
          className="chart-library__primary"
          disabled={
            dragging ||
            (detail
              ? !!existing || props.adding !== null
              : tab === "arrange"
              ? !dirty || saving
              : false)
          }
          onClick={() => {
            if (detail) void add(detail, chosenSetting);
            else if (tab === "arrange") {
              setSaving(true);
              setError(null);
              props
                .onSaveOrder(items.map((item) => item.key))
                .then(props.onClose)
                .catch(() => setError("saveFailed"))
                .finally(() => setSaving(false));
            } else dismiss();
          }}
        >
          {detail
            ? props.adding === detail.id && detail.id !== undefined
              ? t("charts.library.adding")
              : existing
              ? t("charts.library.existingVariant")
              : props.layoutItems.some(item => item.type === detail.type) ? t("charts.library.addAnother") : t("charts.library.add")
            : tab === "arrange"
            ? saving
              ? t("charts.library.saving")
              : t("charts.library.save")
            : t("charts.library.done")}
        </button>
      </footer>
    </dialog>,
    document.body
  );
}
